import React, {useContext, useCallback, useState} from 'react';
import {StateContext} from '../App';
import Timer from './Timer';
import './GameOver.scss';
import {useSelector, useDispatch} from 'react-redux';
import {getDisplayTimeBySeconds} from '../services/timer-service.js';
import {Button} from '@material-ui/core';
import ShareIcon from '@material-ui/icons/Share';
import ReplayIcon from '@material-ui/icons/Replay';
import FlareIcon from '@material-ui/icons/Flare';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import {resetGame} from '../actions/actions';
import {FIRST_LEVEL_TIME, SECONDS_PER_WORD} from '../constants';
import ReactGA from 'react-ga';

const GameOver = () => {
    const stateContext = useContext(StateContext);
    const {timer} = stateContext;
    const words = useSelector((state) => state.words);
    const dispatch = useDispatch();
    const [shareBtnText, setShareBtnText] = useState('Share');

    const secondsDone = getSecondsDone(stateContext.level, timer.seconds);

    const onShareClicked = useCallback(async (event) => {
        ReactGA.event({
            category: 'User',
            action: 'Share Results',
        });
        const rawWords = words.map((word) => word.text);
        const rawLocation = window.location.protocol + '//' + window.location.host + window.location.pathname;
        const title = `Syrch`;
        const text = `I got Syrch level ${stateContext.level} in ${getDisplayTimeBySeconds(secondsDone)}!\n\n${
            rawWords.length
        } words - ${rawWords.join(', ')}`;
        const url = `${rawLocation}?words=${rawWords.join(',')}`;

        const shareData = {
            title,
            text,
        };
        try {
            if (navigator.share) {
                await navigator.share(shareData);
                setShareBtnText('Shared!');
            } else {
                // Copy to clipboard
                await navigator.clipboard.writeText(text);
                navigator.clipboard.readText().then(setShareBtnText('Copied'));
            }
        } catch (err) {
            console.error(err);
        }
    });

    const onNextLevelClicked = useCallback((event) => {
        ReactGA.event({
            category: 'User',
            action: 'Play Next Level',
        });
        stateContext.setNextLevel();
        dispatch(resetGame());
        // Reset the timer and start it again
        stateContext.reset();
        stateContext.startTimer();
    });

    const onPlayAgainClicked = useCallback((event) => {
        ReactGA.event({
            category: 'User',
            action: 'Play Again',
        });
        dispatch(resetGame());
        // Reset the timer and start it again
        stateContext.reset();
        stateContext.startTimer();
    });

    const onNewGameClicked = useCallback(() => {
        window.location = window.location.pathname;
    });

    const params = new URLSearchParams(window.location.search);
    const hasWordsParam = params.has('words');
    const titleText = getTitle(secondsDone);

    return (
        <div className="game-over">
            <div className="game-over-content">
                <h1 className="game-over-title">{titleText}</h1>
                <h2 className="game-over-message">
                    You have completed level {stateContext.level} in <Timer seconds={secondsDone} />
                </h2>
                <Button
                    className="share-btn"
                    variant="contained"
                    color="primary"
                    endIcon={<ShareIcon />}
                    onClick={onShareClicked}
                >
                    {shareBtnText}
                </Button>
                <div style={{margin: '10px 0'}}>and</div>
                <div className="next-action">
                    <Button
                        className="next-level-btn"
                        variant="contained"
                        size="small"
                        color="secondary"
                        endIcon={<LockOpenIcon />}
                        onClick={onNextLevelClicked}
                    >
                        Next Level
                    </Button>
                    <span>or</span>
                    <Button
                        className="play-again-btn"
                        variant="contained"
                        size="small"
                        color="secondary"
                        endIcon={<ReplayIcon />}
                        onClick={onPlayAgainClicked}
                    >
                        Play again
                    </Button>

                    {hasWordsParam && (
                        <Button
                            className="new-game-btn"
                            variant="contained"
                            size="small"
                            color="secondary"
                            endIcon={<FlareIcon />}
                            onClick={onNewGameClicked}
                        >
                            New game
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

const getTitle = (seconds) => {
    let result = 'Nice!';
    if (seconds < 120) {
        result = 'Impressive!';
    } else if (seconds < 240) {
        result = 'Amazing!';
    }

    return result;
};

const getSecondsDone = (level, secondsLeft) => {
    let result = FIRST_LEVEL_TIME - secondsLeft;
    if (level > 1) {
        const total = FIRST_LEVEL_TIME + (level - 1) * SECONDS_PER_WORD;
        result = total - secondsLeft;
    }
    return result;
};

export default GameOver;
